import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Globe, Send, MessageCircle, Clock, CheckCircle, Camera, X } from 'lucide-react';
import { trackContactInquiry, trackConversion, trackFunnelStep } from '../utils/analytics';
import { supabase } from '../admin/lib/supabaseClient';
import { compressImage, uploadFile } from '../admin/utils/storage';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    subject: 'general'
  });
  const [files, setFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Track funnel start
      trackFunnelStep('contact', 'form_started', { subject: formData.subject });
      
      // Track contact form submission
      trackContactInquiry(formData.subject);
      
      // Upload any attached images first (compress + store)
      const uploadedUrls: string[] = [];
      for (const f of files) {
        try {
          const compressed = await compressImage(f, 1);
          const publicUrl = await uploadFile('contact', compressed as File);
          uploadedUrls.push(publicUrl);
        } catch (err) {
          // Skip failed uploads
        }
      }

      // Track if images were attached
      if (uploadedUrls.length > 0) {
        trackFunnelStep('contact', 'images_attached', { image_count: uploadedUrls.length });
      }

      // Save to Supabase database including image URLs when present
      const submissionData: any = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        message: formData.message,
        subject: formData.subject,
        source: 'website_contact',
        status: 'new',
        images: uploadedUrls.length > 0 ? uploadedUrls : undefined
      };

      // Try to add new fields, but don't fail if they don't exist yet
      try {
        if (formData.subject === 'repair' || formData.subject === 'quote') {
          submissionData.issues = [formData.subject];
        }
      } catch (err) {
        console.warn('Database may not have latest schema - continuing without issues field');
      }

      const { error } = await supabase.from('contact_submissions').insert([submissionData]);

      if (error) throw error;
      
      // Track successful conversion
      trackConversion('contact', undefined, { 
        subject: formData.subject,
        has_attachments: uploadedUrls.length > 0 
      });
      trackFunnelStep('contact', 'form_completed', { subject: formData.subject });
      
      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', message: '', subject: 'general' });
  setFiles([]);
      
      // Reset success message after 3 seconds
      setTimeout(() => setSubmitStatus('idle'), 3000);
    } catch (error) {
      trackFunnelStep('contact', 'form_error', { error: String(error) });
      setIsSubmitting(false);
      setSubmitStatus('error');
      
      // Reset error message after 3 seconds
      setTimeout(() => setSubmitStatus('idle'), 3000);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    setFiles(prev => [...prev, ...Array.from(e.target.files)]);
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Visit Our Autobody Shop',
      content: '461 Jasmyn Street Cnr, Dykor St',
      subcontent: 'Silverton, Pretoria, 0184',
      link: 'https://maps.google.com/?q=461+Jasmyn+Street+Cnr+Dykor+St+Silverton+Pretoria+0184',
      linkText: 'Get Directions'
    },
    {
      icon: Phone,
      title: 'Call Us',
      content: '+27 72 998 1365',
      subcontent: 'Monday to Friday, 8:00 AM - 5:00 PM',
      link: 'tel:+27729981365',
      linkText: 'Call Now'
    },
    {
      icon: MessageCircle,
      title: 'WhatsApp Us',
      content: '+27 72 998 1365',
      subcontent: 'Quick quotes and updates',
      link: 'https://wa.me/27729981365',
      linkText: 'WhatsApp Now'
    },
    {
      icon: Mail,
      title: 'Email Us',
      content: 'mike@silvertoncc.co.za',
      subcontent: 'We reply within 24 hours',
      link: 'mailto:mike@silvertoncc.co.za',
      linkText: 'Send Email'
    },
    {
      icon: Globe,
      title: 'Website',
      content: 'classicminis.co.za',
      subcontent: 'Online quotes & gallery',
      link: 'http://classicminis.co.za',
      linkText: 'Visit Site'
    }
  ];

  const businessHours = [
    { day: 'Monday - Friday', hours: '8:00 AM - 5:00 PM' },
    { day: 'Saturday & Sunday', hours: 'Closed' },
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const buildWhatsAppLink = (name?: string, phone?: string, message?: string) => {
    const businessNumber = '27729981365';
    const textParts: string[] = [];
    if (name) textParts.push(`Name: ${name}`);
    if (phone) textParts.push(`Phone: ${phone}`);
    if (message) textParts.push(`Message: ${message}`);
    // Build raw text with real newlines, then encode once. Also defensively decode first.
    const raw = textParts.length ? `Hello,\n\n${textParts.join('\n')}` : 'Hello, I would like to get in touch.';
    const normalized = decodeURIComponent(String(raw)).replace(/\r\n|\r/g, '\n');
    return `https://wa.me/${businessNumber}?text=${encodeURIComponent(normalized)}`;
  };

  return (
    <section id="contact" className="section-padding bg-white relative overflow-hidden">
      {/* Background Mini Graphics */}
      <div className="absolute bottom-10 left-10 opacity-10">
        <svg width="150" height="75" viewBox="0 0 150 75" className="text-mini-red bounce-gentle">
          <path d="M15 55 C10 50, 10 25, 15 20 L30 15 C40 10, 60 5, 75 5 C90 5, 110 10, 120 15 L135 20 C140 25, 140 50, 135 55 L130 60 C130 65, 125 70, 120 70 L110 70 C105 70, 100 65, 100 60 L50 60 C50 65, 45 70, 40 70 L30 70 C25 70, 20 65, 20 60 Z" fill="currentColor" />
          <circle cx="40" cy="65" r="8" />
          <circle cx="110" cy="65" r="8" />
          <path d="M30 20 C40 15, 55 5, 75 5 C95 5, 110 15, 120 20 L115 35 L35 35 Z" fill="currentColor" opacity="0.7" />
        </svg>
      </div>
      
      <div className="container-custom relative z-10">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <a 
            href={buildWhatsAppLink(formData.name, formData.phone, formData.message)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-2 bg-mini-red/10 text-mini-red px-4 py-2 rounded-full text-sm font-medium mb-6 hover:bg-mini-red/20 transition-colors"
          >
            <MessageCircle size={16} />
            <span>Get In Touch</span>
          </a>
          
          <h2 className="text-3xl sm:text-4xl section-heading text-gray-900 mb-6">
            Let's Discuss Your Mini Project
          </h2>
          
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Ready to restore your classic Mini? Get in touch for a consultation, quote, or to discuss your project needs.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Form - Takes 2/3 of the width */}
          <motion.div
            className="lg:col-span-2"
            variants={fadeInUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h3 className="text-2xl font-heading font-bold text-gray-900 mb-6">
                Send Us a Message
              </h3>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mini-red focus:border-transparent bg-white text-gray-900 transition-colors"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mini-red focus:border-transparent bg-white text-gray-900 transition-colors"
                      placeholder="+27 12 345 6789"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mini-red focus:border-transparent bg-white text-gray-900 transition-colors"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                    Subject
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mini-red focus:border-transparent bg-white text-gray-900 transition-colors"
                  >
                    <option value="general">General Inquiry</option>
                    <option value="restoration">Full Restoration</option>
                    <option value="repair">Repair Service</option>
                    <option value="parts">Parts Request</option>
                    <option value="quote">Quote Request</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mini-red focus:border-transparent bg-white text-gray-900 transition-colors resize-none"
                    placeholder="Tell us about your Mini and what you need help with..."
                  />
                </div>

                {/* Photo Attachment Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Attach Photos (Optional)
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    Upload photos of your Mini, damage, or parts to help us provide better assistance.
                  </p>
                  
                  <div className="flex items-center gap-3 mb-3">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <motion.button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg border border-gray-300 transition-colors"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Camera size={18} />
                      <span>Choose Photos</span>
                    </motion.button>
                    {files.length > 0 && (
                      <span className="text-sm text-gray-600">
                        {files.length} photo{files.length !== 1 ? 's' : ''} selected
                      </span>
                    )}
                  </div>

                  {/* Photo Preview Grid */}
                  {files.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                      {files.map((file, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={URL.createObjectURL(file)}
                            alt={`Preview ${index + 1}`}
                            className="w-full h-20 object-cover rounded-lg border border-gray-200"
                          />
                          <motion.button
                            type="button"
                            onClick={() => setFiles(files.filter((_, i) => i !== index))}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                          >
                            <X size={12} />
                          </motion.button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <motion.button
                  type="submit"
                  disabled={isSubmitting || submitStatus === 'success'}
                  className={`w-full flex items-center justify-center space-x-2 py-3 px-6 rounded-lg font-semibold transition-all duration-300 ${
                    submitStatus === 'success'
                      ? 'bg-green-500 text-white'
                      : submitStatus === 'error'
                      ? 'bg-red-500 text-white'
                      : 'bg-mini-red hover:bg-red-700 text-white'
                  } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                  whileHover={!isSubmitting ? { scale: 1.02 } : {}}
                  whileTap={!isSubmitting ? { scale: 0.98 } : {}}
                >
                  {submitStatus === 'success' ? (
                    <>
                      <CheckCircle size={20} />
                      <span>Message Sent!</span>
                    </>
                  ) : submitStatus === 'error' ? (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Error - Try Again</span>
                    </>
                  ) : isSubmitting ? (
                    <>
                      <motion.div
                        className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Send size={20} />
                      <span>Send Message</span>
                    </>
                  )}
                </motion.button>
                  <a
                    href={buildWhatsAppLink(formData.name, formData.phone, formData.message)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full inline-flex items-center justify-center space-x-2 py-3 px-6 rounded-lg font-semibold transition-all duration-300 bg-green-600 hover:bg-green-700 text-white mt-3"
                  >
                    <Camera size={18} />
                    <span>Contact via WhatsApp</span>
                  </a>
              </form>
            </div>
          </motion.div>

          {/* Contact Information - Takes 1/3 of the width */}
          <motion.div
            className="space-y-6"
            variants={fadeInUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {/* Contact Details */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h4 className="font-semibold text-gray-900 mb-4">Contact Information</h4>
              <div className="space-y-4">
                {contactInfo.map((info, index) => (
                  <motion.div
                    key={index}
                    className="flex items-start space-x-3"
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                  >
                    <div className="flex-shrink-0 w-10 h-10 bg-mini-red/10 rounded-lg flex items-center justify-center">
                      <info.icon size={18} className="text-mini-red" />
                    </div>
                    <div className="flex-1">
                      <h5 className="font-medium text-gray-900 text-sm">
                        {info.title}
                      </h5>
                      <a
                        href={info.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-600 hover:text-mini-red text-sm transition-colors"
                      >
                        {info.content}
                      </a>
                      <p className="text-xs text-gray-500">
                        {info.subcontent}
                      </p>
                      <a
                        href={info.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-mini-red hover:text-red-700 text-xs font-medium mt-1 transition-colors"
                      >
                        {info.linkText}
                      </a>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Business Hours */}
            <motion.div
              className="bg-white rounded-2xl shadow-lg p-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="flex items-center space-x-2 mb-4">
                <Clock size={18} className="text-mini-red" />
                <h4 className="font-semibold text-gray-900">
                  Business Hours
                </h4>
              </div>
              <div className="space-y-2">
                {businessHours.map((schedule, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <span className="text-gray-600 text-sm">
                      {schedule.day}
                    </span>
                    <span className="font-medium text-gray-900 text-sm">
                      {schedule.hours}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Google Maps - Full width below */}
        <motion.div
          className="mt-8"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <h3 className="text-xl font-heading font-bold text-gray-900 mb-4 text-center">
            Find Our Autobody Shop
          </h3>
          <div className="rounded-2xl overflow-hidden shadow-lg max-w-4xl mx-auto relative">
            <iframe
              src="https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=461+Jasmyn+Street+Cnr+Dykor+St+Silverton+Pretoria+0184+South+Africa&maptype=satellite&zoom=18"
              width="100%"
              height="250"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Gerrie's Classic Minis Location - 461 Jasmyn Street Cnr, Dykor St, Silverton, Pretoria"
              className="w-full h-[250px]"
            />

            {/* Small overlay button to open full map in a new tab */}
            <a
              href={contactInfo[0].link}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute top-3 right-3 bg-white/90 text-mini-red hover:bg-white px-3 py-1 rounded-lg shadow-md text-xs font-medium flex items-center gap-2"
              aria-label="Open Gerrie's Classic Minis location in Google Maps"
            >
              <MapPin size={14} />
              <span>Open</span>
            </a>
          </div>

          <div className="max-w-4xl mx-auto mt-3 text-center">
            <a
              href={contactInfo[0].link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-mini-red text-white px-4 py-2 rounded-lg shadow hover:bg-red-700 transition-colors"
            >
              <MapPin size={16} />
              <span>Open in Google Maps</span>
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
